/**
 * Fase 2 Sub-release 2.4b — Sezione "Usati velati" per il cliente.
 *
 * Mostra fino a 3 vetture usate coerenti con il canone di riferimento del cliente,
 * ma con effetto sfocato + overlay. Al click si apre un Dialog per richiedere lo
 * sblocco al consulente (usa /api/client/support-request per notificare).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Lock, Sparkles, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsatiVelatiSection({ canoneRiferimento = 0, limit = 3 }) {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogVeicolo, setDialogVeicolo] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/client/veicoli-suggeriti`, {
          headers: getAuthHeader(),
          params: { tipo: 'usato', limit, canone_base_riferimento: canoneRiferimento || 0 },
        });
        if (cancelled) return;
        setItems(res.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canoneRiferimento, limit, getAuthHeader]);

  const handleOpen = (v) => {
    setDialogVeicolo(v);
    setMessage(`Ciao, sono interessato/a all'usato ${v.marca || ''} ${v.modello || ''}. Puoi sbloccarmi tutti i dettagli e la rata?`);
    setDialogOpen(true);
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Scrivi un messaggio per il tuo consulente');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/client/support-request`,
        { message: message.trim() },
        { headers: getAuthHeader() },
      );
      toast.success('Richiesta inviata al tuo consulente. Ti risponderà a breve!');
      setDialogOpen(false);
      setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell&apos;invio richiesta');
    } finally {
      setSending(false);
    }
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <>
      <Card data-testid="usati-velati-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Altre opportunità: Auto Usate Selezionate
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Selezionate per te in base al canone che stai valutando. Contatta il consulente per accedere ai dettagli.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((v) => (
              <div
                key={v.id}
                className="relative rounded-xl overflow-hidden border-2 border-amber-200 bg-white shadow-sm group cursor-pointer transition"
                onClick={() => handleOpen(v)}
                data-testid={`usato-velato-card-${v.id}`}
              >
                {/* Card content sfocata */}
                <div className="p-4 space-y-2 filter blur-sm select-none pointer-events-none">
                  <div className="h-24 bg-slate-100 rounded" />
                  <p className="text-xs text-slate-500 uppercase">{v.marca}</p>
                  <p className="text-lg font-bold text-slate-800">{v.modello}</p>
                  {v.versione && <p className="text-xs text-slate-600 line-clamp-1">{v.versione}</p>}
                  <div className="border-t pt-2">
                    <p className="text-[10px] text-slate-400 uppercase">Canone</p>
                    <p className="text-lg font-bold text-slate-800">€{Math.round(v.canone_base || 0).toLocaleString('it-IT')}<span className="text-xs font-normal">/mese</span></p>
                  </div>
                </div>

                {/* Overlay Lock */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px] group-hover:bg-white/60 transition">
                  <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-lg mb-2 group-hover:scale-110 transition-transform">
                    <Lock className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">Contenuto riservato</p>
                  <p className="text-xs text-slate-600 mt-0.5">Clicca per richiedere lo sblocco</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Richiedi Sblocco */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) setDialogOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="usato-sblocco-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Richiedi lo sblocco
            </DialogTitle>
            <DialogDescription>
              {dialogVeicolo ? (
                <>Il tuo consulente riceverà una notifica per <strong>{dialogVeicolo.marca} {dialogVeicolo.modello}</strong> e ti mostrerà tutti i dettagli.</>
              ) : 'Il tuo consulente ti risponderà a breve.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="text-sm"
            data-testid="usato-sblocco-message"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={sending} data-testid="btn-usato-sblocco-cancel">
              Annulla
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-usato-sblocco-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invia richiesta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
